import React, { useState, useContext, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';
import { useRequireLogin } from '../../utils/requireLogin';
import { apiFetch, parseApiResponse, readApiError, invalidateApiCache } from '../../utils/apiClient';
import { resolveImageSrc } from '../../utils/imageUtils';
import { toTitleCase } from '../../utils/textUtils';
import { showCoinAwardToast, queueCoinAwardToast, flushPendingCoinAwardToast } from '../../utils/collectorRewardsUtils';
import { useNotifications } from '../../components/NotificationSystem/NotificationSystem';
import { useMessages } from '../../components/Messages/MessagesContext';
import SellerTrustSummary from '../../components/SellerTrustSummary/SellerTrustSummary';
import MsvDetailShell from '../../components/MsvDetailShell/MsvDetailShell';
import MsvSelect from '../../components/MsvSelect/MsvSelect';
import ImageCarousel from '../../components/ImageCarousel/ImageCarousel';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import MarkAsSoldDialog from '../../components/MarkAsSoldDialog/MarkAsSoldDialog';
import { US_STATES, normalizeUsState } from '../../constants/usStates';
import '../ViewCollectionItem/ViewCollectionItem.css';
import './ViewMarketplaceItem.css';

const STATE_OPTIONS = US_STATES;

const formatPrice = (value) => {
    const num = Number(value || 0);
    if (Number.isNaN(num)) return '$0';
    if (num % 1 === 0) return `$${num.toLocaleString('en-US')}`;
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const statusLabel = (status) => {
    if (status === 'UnderReview') return 'Under Review';
    if (status === 'Removed') return 'Paused';
    return status || 'Active';
};

/** One-line preview: line breaks become spaces so words don't run together. */
const descriptionToSingleLine = (text) => {
    if (!text) return '';
    return String(text).replace(/\r?\n+/g, ' ');
};

const buildEditFormFromItem = (data) => ({
    title: data?.title || '',
    description: data?.description || '',
    price: data?.price != null ? String(data.price) : '',
    shippingAvailable: Boolean(data?.shippingAvailable),
    localPickupAvailable: data?.localPickupAvailable !== false,
    shippingCost: data?.shippingCost != null ? String(data.shippingCost) : '',
    city: data?.city || '',
    state: normalizeUsState(data?.state || ''),
    externalSiteUrl: data?.externalSiteUrl || '',
});

const formatFulfillmentLabel = (data) => {
    if (!data) return '—';
    const parts = [];
    if (data.shippingAvailable) {
        parts.push(
            data.shippingCost
                ? `Shipping · ${formatPrice(data.shippingCost)}`
                : 'Shipping'
        );
    }
    if (data.localPickupAvailable !== false) {
        parts.push('Local pickup');
    }
    return parts.length ? parts.join(' · ') : 'Contact seller';
};

const ViewMarketplaceItem = () => {
    const { user, setUser } = useContext(UserContext);
    const { authReady, isAuthenticated } = useRequireLogin('Please log in to view marketplace listings.');
    const { startConversation } = useMessages();
    const { success, error: notifyError } = useNotifications();
    const [loading, setLoading] = useState(true);
    const [item, setItem] = useState(null);
    const [imageData, setImageData] = useState(null);
    const [image2Data, setImage2Data] = useState(null);
    const { marketplaceItemId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [error, setError] = useState(null);
    const [messageText, setMessageText] = useState('Hi, I am interested. Is this still available?');
    const [messagingSeller, setMessagingSeller] = useState(false);
    const [showRelistConfirm, setShowRelistConfirm] = useState(false);
    const [showPublishConfirm, setShowPublishConfirm] = useState(false);
    const [showMoveToCollectionConfirm, setShowMoveToCollectionConfirm] = useState(false);
    const [showMarkSoldDialog, setShowMarkSoldDialog] = useState(false);
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
    const [relistingListing, setRelistingListing] = useState(false);
    const [publishingListing, setPublishingListing] = useState(false);
    const [movingToCollection, setMovingToCollection] = useState(false);
    const [markingSold, setMarkingSold] = useState(false);
    const [removingListing, setRemovingListing] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [savingEdit, setSavingEdit] = useState(false);
    const [editForm, setEditForm] = useState(buildEditFormFromItem(null));
    const [editErrors, setEditErrors] = useState({});
    const [showDescriptionPopup, setShowDescriptionPopup] = useState(false);
    const [descriptionDraft, setDescriptionDraft] = useState('');
    const descriptionPopupRef = useRef(null);

    useEffect(() => {
        flushPendingCoinAwardToast(success);
    }, [success]);

    useEffect(() => {
        const stored = localStorage.getItem('user');
        if (!user && stored) {
            setUser(JSON.parse(stored));
        }
    }, [user, setUser]);

    useEffect(() => {
        if (!authReady || !isAuthenticated) return;
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await apiFetch('/Marketplace/GetMarketplaceItemById', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        marketplaceItemId: Number(marketplaceItemId),
                        userId: user?.userId ?? null,
                    }),
                });
                const data = await parseApiResponse(res);
                if (cancelled) return;
                // Deleted listings return Ok(null) / empty body — treat as not found.
                if (!res.ok || !data || data.marketplaceItemId == null) {
                    throw new Error('This listing is no longer available.');
                }
                setItem(data);
                setImageData(resolveImageSrc(data));
                setImage2Data(
                    (typeof data.imageUrl2 === 'string' && data.imageUrl2) ||
                    (typeof data.ImageUrl2 === 'string' && data.ImageUrl2) ||
                    null
                );
                const status = data.listingStatus || 'Active';
                const canOpenEdit =
                    status === 'Rejected' ||
                    status === 'Active' ||
                    status === 'Expired' ||
                    data.isExpired;
                const openEdit =
                    data.isOwner &&
                    canOpenEdit &&
                    new URLSearchParams(location.search).get('edit') === '1';
                if (openEdit) {
                    setEditForm(buildEditFormFromItem(data));
                    setEditErrors({});
                    setIsEditing(true);
                }
            } catch (err) {
                if (!cancelled) setError(err.message || 'Failed to load listing');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [marketplaceItemId, authReady, isAuthenticated, user?.userId, location.search]);

    const updateStatus = async (listingStatus, renewListing = false, saleDetails = null) => {
        setUpdatingStatus(true);
        try {
            const payload = {
                marketplaceItemId: Number(marketplaceItemId),
                userId: String(user.userId),
                listingStatus,
                renewListing,
            };

            if (saleDetails) {
                payload.soldOnPlatform = saleDetails.soldOnPlatform;
                if (saleDetails.soldOnPlatform) {
                    payload.soldToUsername = saleDetails.soldToUsername;
                } else {
                    payload.soldElsewhereDetail = saleDetails.soldElsewhereDetail;
                    if (saleDetails.soldToName) payload.soldToName = saleDetails.soldToName;
                }
            }

            const response = await apiFetch('/Marketplace/UpdateMarketplaceStatus', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const result = await parseApiResponse(response);
            if (!response.ok || result?.success === false) {
                notifyError(readApiError(result, 'Could not update listing.'));
                return false;
            }

            if (result?.coinAward?.awarded) {
                queueCoinAwardToast(result.coinAward);
            } else {
                showCoinAwardToast(success, result?.coinAward);
            }

            // Browse uses a short TTL cache; drop it so Back keeps filters but shows fresh results.
            invalidateApiCache('/Marketplace/SearchMarketplace');

            const refreshed = await apiFetch('/Marketplace/GetMarketplaceItemById', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    marketplaceItemId: Number(marketplaceItemId),
                    userId: user?.userId ?? null,
                }),
            });
            if (refreshed.ok) {
                const data = await parseApiResponse(refreshed);
                if (data?.marketplaceItemId != null) {
                    setItem(data);
                    setImageData(resolveImageSrc(data));
                    setImage2Data(
                        (typeof data.imageUrl2 === 'string' && data.imageUrl2) ||
                        (typeof data.ImageUrl2 === 'string' && data.ImageUrl2) ||
                        null
                    );
                }
            }
            return result || true;
        } finally {
            setUpdatingStatus(false);
        }
    };

    const confirmRelistListing = async () => {
        setRelistingListing(true);
        try {
            const result = await updateStatus('Active');
            if (result) {
                setShowRelistConfirm(false);
                const nextStatus = result?.listingStatus || 'Active';
                success(
                    nextStatus === 'UnderReview'
                        ? 'Listing submitted for admin approval.'
                        : 'Listing relisted for 30 days.'
                );
            }
        } finally {
            setRelistingListing(false);
        }
    };

    const confirmPublishListing = async () => {
        setPublishingListing(true);
        try {
            const ok = await updateStatus('UnderReview');
            if (ok) {
                setShowPublishConfirm(false);
                setIsEditing(false);
                success('Listing submitted for admin approval.');
            }
        } finally {
            setPublishingListing(false);
        }
    };

    const startEditing = () => {
        setEditForm(buildEditFormFromItem(item));
        setEditErrors({});
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setIsEditing(false);
        setEditErrors({});
        setShowDescriptionPopup(false);
        if (new URLSearchParams(location.search).get('edit') === '1') {
            navigate(`/marketplaceItem/${marketplaceItemId}`, { replace: true });
        }
    };

    const openDescriptionPopup = () => {
        setDescriptionDraft(editForm.description || '');
        setShowDescriptionPopup(true);
    };

    const closeDescriptionPopup = () => {
        setShowDescriptionPopup(false);
    };

    const saveDescriptionPopup = () => {
        setEditForm((prev) => ({
            ...prev,
            description: descriptionDraft.slice(0, 500),
        }));
        setShowDescriptionPopup(false);
    };

    useEffect(() => {
        if (!showDescriptionPopup) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') closeDescriptionPopup();
        };
        window.addEventListener('keydown', onKeyDown);
        const focusTimer = window.setTimeout(() => descriptionPopupRef.current?.focus(), 0);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            window.clearTimeout(focusTimer);
        };
    }, [showDescriptionPopup]);

    const handleEditChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditForm((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const saveEdit = async () => {
        const nextErrors = {};
        const title = (editForm.title || '').trim();
        const priceNum = Number(editForm.price);
        if (!title) nextErrors.title = 'Title is required';
        if (title.length > 80) nextErrors.title = 'Title must be 80 characters or less';
        if (editForm.price === '' || Number.isNaN(priceNum) || priceNum < 0) {
            nextErrors.price = 'Enter a valid price';
        }
        if (!(editForm.city || '').trim()) nextErrors.city = 'City is required';
        if (!(editForm.state || '').trim()) nextErrors.state = 'State is required';
        if (!editForm.shippingAvailable && !editForm.localPickupAvailable) {
            nextErrors.fulfillment = 'Select shipping and/or local pickup';
        }
        setEditErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) return;

        setSavingEdit(true);
        try {
            const response = await apiFetch('/Marketplace/UpdateMarketplaceItem', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    marketplaceItemId: Number(marketplaceItemId),
                    userId: String(user.userId),
                    title,
                    description: (editForm.description || '').trim(),
                    price: priceNum,
                    shippingAvailable: Boolean(editForm.shippingAvailable),
                    localPickupAvailable: Boolean(editForm.localPickupAvailable),
                    shippingCost: editForm.shippingAvailable
                        ? Number(editForm.shippingCost || 0)
                        : 0,
                    city: (editForm.city || '').trim(),
                    state: (editForm.state || '').trim(),
                    externalSiteUrl: (editForm.externalSiteUrl || '').trim() || null,
                }),
            });
            const result = await parseApiResponse(response);
            if (!response.ok || result === false) {
                notifyError(readApiError(result, 'Could not save this listing.'));
                return;
            }

            invalidateApiCache('/Marketplace/SearchMarketplace');

            const refreshed = await apiFetch('/Marketplace/GetMarketplaceItemById', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    marketplaceItemId: Number(marketplaceItemId),
                    userId: user?.userId ?? null,
                }),
            });
            if (refreshed.ok) {
                const data = await parseApiResponse(refreshed);
                if (data?.marketplaceItemId != null) {
                    setItem(data);
                    setImageData(resolveImageSrc(data));
                    setImage2Data(
                        (typeof data.imageUrl2 === 'string' && data.imageUrl2) ||
                        (typeof data.ImageUrl2 === 'string' && data.ImageUrl2) ||
                        null
                    );
                }
            }

            setIsEditing(false);
            setEditErrors({});
            if (new URLSearchParams(location.search).get('edit') === '1') {
                navigate(`/marketplaceItem/${marketplaceItemId}`, { replace: true });
            }
            success(
                status === 'Rejected'
                    ? 'Listing submitted for admin approval.'
                    : 'Listing updated.'
            );
        } finally {
            setSavingEdit(false);
        }
    };

    const confirmMoveToCollection = async () => {
        setMovingToCollection(true);
        try {
            const response = await apiFetch('/Marketplace/MoveRemovedListingToCollection', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    marketplaceItemId: Number(marketplaceItemId),
                    userId: String(user.userId),
                }),
            });
            const result = await parseApiResponse(response);
            if (!response.ok || result?.success === false) {
                notifyError(readApiError(result, 'Could not delete this listing from your collection.'));
                return;
            }

            success(
                (item?.listingStatus || '') === 'UnderReview'
                    ? 'Listing cancelled. Item is still in your collection.'
                    : 'Listing deleted. Item is still in your collection.'
            );
            setShowMoveToCollectionConfirm(false);
            invalidateApiCache('/Marketplace/SearchMarketplace');
            navigate('/mymarketplacelistings', { replace: true });
        } finally {
            setMovingToCollection(false);
        }
    };

    const confirmMarkSold = async (saleDetails) => {
        setMarkingSold(true);
        try {
            const ok = await updateStatus('Sold', false, saleDetails);
            if (ok) {
                if (saleDetails?.soldOnPlatform) {
                    success('Marked as sold. Item moved to the buyer’s collection.');
                } else {
                    success('Marked as sold. Item removed from your collection.');
                }
                setShowMarkSoldDialog(false);
            }
        } finally {
            setMarkingSold(false);
        }
    };

    const confirmRemoveListing = async () => {
        setRemovingListing(true);
        try {
            const ok = await updateStatus('Removed');
            if (ok) {
                setShowRemoveConfirm(false);
                success('Listing paused. It stays in My Listings if you want to relist it.');
            }
        } finally {
            setRemovingListing(false);
        }
    };

    const buildMessageWithListingLink = (text) => {
        const trimmed = text.trim();
        const listingUrl = `${window.location.origin}/marketplaceItem/${marketplaceItemId}`;
        return `${trimmed}\n\nListing: ${listingUrl}`;
    };

    const handleMessageSeller = async () => {
        if (!item?.userId) return;
        setMessagingSeller(true);
        try {
            await startConversation({
                otherUserId: item.userId,
                marketplaceItemId: Number(marketplaceItemId),
                initialMessage: buildMessageWithListingLink(messageText),
            });
        } catch (err) {
            console.error('Failed to open chat:', err);
        } finally {
            setMessagingSeller(false);
        }
    };

    if (!authReady || !isAuthenticated) return null;

    if (loading) {
        return (
            <MsvDetailShell
                onBack={() => navigate('/marketplace', { state: { restoreMarketplaceScroll: true } })}
                backLabel="Marketplace"
                showFooter={false}
            >
                <div className="view-collection-container vmi-page" aria-busy="true" aria-live="polite">
                    <div className="vmi-loading-skeleton" aria-hidden="true">
                        <div className="vmi-skel-line vmi-skel-title" />
                        <div className="vmi-skel-showcase">
                            <div className="vmi-skel-media" />
                            <div className="vmi-skel-info">
                                <div className="vmi-skel-line" />
                                <div className="vmi-skel-line vmi-skel-short" />
                                <div className="vmi-skel-line" />
                            </div>
                        </div>
                    </div>
                </div>
            </MsvDetailShell>
        );
    }

    if (error) {
        return (
            <MsvDetailShell
                error={error}
                showFooter={false}
                onBack={() => navigate('/mymarketplacelistings')}
                backLabel="My Listings"
            />
        );
    }

    if (!item) {
        return (
            <MsvDetailShell
                error="No listing found."
                showFooter={false}
                onBack={() => navigate('/mymarketplacelistings')}
                backLabel="My Listings"
            />
        );
    }

    const price = item.price ?? 0;
    const isOwner = item.isOwner;
    const placeLabel = [item.city, item.state].filter(Boolean).join(', ');
    const canMessage = !isOwner && item.listingStatus === 'Active' && !item.isExpired;
    const showMessagePanel = canMessage || isOwner;
    const categoryLine = [
        item.mainCategory,
        item.subCategory,
        item.subSubCategory,
        item.subSubSubCategory,
    ].filter(Boolean).join(' › ');
    const shippingLabel = formatFulfillmentLabel(item);
    const status = item.listingStatus || 'Active';
    const showStatusBadge = status !== 'Active' || item.isExpired;
    const canManageListing = status === 'Active' || status === 'Expired' || item.isExpired;
    const canRemove =
        status === 'Active' ||
        status === 'Expired' ||
        item.isExpired;
    const canRelist = status === 'Removed';
    const canPublishRejected = isOwner && status === 'Rejected';
    const canEditListing =
        isOwner &&
        (status === 'Rejected' || status === 'Active' || status === 'Expired' || item.isExpired);
    const canMoveToCollection =
        status === 'Removed' || status === 'Rejected' || status === 'UnderReview';
    const canRenew =
        item.isExpired ||
        status === 'Expired' ||
        (item.renewalDueUnix > 0 && item.renewalDueUnix <= Math.floor(Date.now() / 1000));
    const ownerActionsBusy =
        updatingStatus ||
        relistingListing ||
        publishingListing ||
        movingToCollection ||
        savingEdit ||
        removingListing;

    return (
        <>
            <MsvDetailShell
                onBack={() => navigate('/marketplace', { state: { restoreMarketplaceScroll: true } })}
                backLabel="Marketplace"
            >
                <div className="view-collection-container vmi-page">
                    <div className="no-edit">
                        <h2 className="collection-title">
                            {isEditing
                                ? 'Edit listing'
                                : toTitleCase(item.title)}
                        </h2>

                        {showStatusBadge && !isEditing && (
                            <div className="vmi-status-row">
                                <span className={`listing-badge status-${(item.isExpired ? 'expired' : status).toLowerCase()}`}>
                                    {item.isExpired ? 'Expired' : statusLabel(status)}
                                </span>
                            </div>
                        )}

                        {!isEditing && item.isExpired && (
                            <p className="expired-notice">This listing has expired and needs renewal.</p>
                        )}
                        {!isEditing && status === 'UnderReview' && (
                            <p className="expired-notice">
                                This listing is waiting for admin approval. It is hidden from the marketplace until approved.
                            </p>
                        )}
                        {!isEditing && status === 'Rejected' && (
                            <div className="vmi-notice-stack">
                                <p className="expired-notice">
                                    This listing was not approved and is hidden from the marketplace. Edit and save, or resubmit for admin approval.
                                </p>
                                {item.rejectionReason ? (
                                    <p className="expired-notice vmi-rejection-note">
                                        <strong>Admin note:</strong> {item.rejectionReason}
                                    </p>
                                ) : null}
                            </div>
                        )}
                        {!isEditing && status === 'Removed' && (
                            <p className="expired-notice">This listing is paused. Relist it or delete the listing and keep the item in your collection.</p>
                        )}

                        {(isOwner && (canManageListing || canRelist || canPublishRejected || canEditListing || canMoveToCollection || isEditing)) || item.externalSiteUrl ? (
                            <div className="vci-action-row vmi-owner-action-row">
                                {isOwner && isEditing && (
                                    <>
                                        <button
                                            type="button"
                                            className="view-button vci-btn-primary"
                                            disabled={ownerActionsBusy}
                                            onClick={saveEdit}
                                        >
                                            {savingEdit ? 'Saving…' : 'Save changes'}
                                        </button>
                                        <button
                                            type="button"
                                            className="view-button"
                                            disabled={ownerActionsBusy}
                                            onClick={cancelEditing}
                                        >
                                            Cancel
                                        </button>
                                    </>
                                )}
                                {isOwner && !isEditing && canEditListing && (
                                    <button
                                        type="button"
                                        className="view-button vci-btn-primary"
                                        disabled={ownerActionsBusy}
                                        onClick={startEditing}
                                    >
                                        Edit listing
                                    </button>
                                )}
                                {isOwner && !isEditing && canManageListing && (
                                    <>
                                        <button
                                            type="button"
                                            className="view-button"
                                            disabled={!canRenew || updatingStatus}
                                            title={
                                                canRenew
                                                    ? undefined
                                                    : 'Renew becomes available after the 30-day listing period ends'
                                            }
                                            onClick={() => updateStatus('Active', true)}
                                        >
                                            Renew for 30 days
                                        </button>
                                        <button
                                            type="button"
                                            className="view-button"
                                            disabled={updatingStatus || markingSold || removingListing}
                                            onClick={() => setShowMarkSoldDialog(true)}
                                        >
                                            Mark as sold
                                        </button>
                                        {canRemove && (
                                            <button
                                                type="button"
                                                className="delete-button vmi-delete-listing"
                                                disabled={updatingStatus || markingSold || removingListing}
                                                onClick={() => setShowRemoveConfirm(true)}
                                            >
                                                Pause listing
                                            </button>
                                        )}
                                    </>
                                )}
                                {isOwner && !isEditing && canPublishRejected && (
                                    <button
                                        type="button"
                                        className="view-button"
                                        disabled={ownerActionsBusy}
                                        onClick={() => setShowPublishConfirm(true)}
                                    >
                                        Resubmit for approval
                                    </button>
                                )}
                                {isOwner && !isEditing && canRelist && (
                                    <button
                                        type="button"
                                        className="view-button vci-btn-primary"
                                        disabled={ownerActionsBusy}
                                        onClick={() => setShowRelistConfirm(true)}
                                    >
                                        Relist for 30 days
                                    </button>
                                )}
                                {isOwner && !isEditing && canMoveToCollection && (
                                    <button
                                        type="button"
                                        className="view-button"
                                        disabled={ownerActionsBusy}
                                        onClick={() => setShowMoveToCollectionConfirm(true)}
                                    >
                                        {status === 'UnderReview' ? 'Cancel listing' : 'Delete listing'}
                                    </button>
                                )}
                                {!isEditing && item.externalSiteUrl && (
                                    <button
                                        type="button"
                                        className="view-button"
                                        onClick={() => window.open(item.externalSiteUrl, '_blank')}
                                    >
                                        Open external link
                                    </button>
                                )}
                            </div>
                        ) : null}

                        <div className="vci-showcase msv-detail-card">
                            <div className="image-container msv-detail-media">
                                {imageData ? (
                                    <ImageCarousel
                                        images={[imageData, image2Data].filter(Boolean)}
                                        alt={item.title}
                                    />
                                ) : (
                                    <div className="marketplace-detail-no-image">No image available</div>
                                )}
                            </div>

                            <div className="text-container msv-detail-info">
                                {isEditing ? (
                                    <div className="vmi-edit-form">
                                        <div className="sign-up-form-group">
                                            <label>
                                                <span>
                                                    Title<span className="asterisk">*</span>
                                                </span>
                                                <span className="vmi-char-count">{editForm.title.length}/80</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="title"
                                                value={editForm.title}
                                                onChange={(e) => {
                                                    if (e.target.value.length <= 80) handleEditChange(e);
                                                }}
                                                className={editErrors.title ? 'error' : ''}
                                            />
                                            {editErrors.title && <p className="sign-up-error">{editErrors.title}</p>}
                                        </div>
                                        <div className="sign-up-form-group">
                                            <label>
                                                Description
                                                <span className="vmi-char-count">
                                                    {(showDescriptionPopup ? descriptionDraft : editForm.description).length}/500
                                                </span>
                                            </label>
                                            <input
                                                type="text"
                                                readOnly
                                                value={descriptionToSingleLine(editForm.description)}
                                                placeholder="Tap to add description…"
                                                onClick={openDescriptionPopup}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        event.preventDefault();
                                                        openDescriptionPopup();
                                                    }
                                                }}
                                                className="vmi-description-trigger"
                                                aria-haspopup="dialog"
                                                aria-label="Description. Tap to edit."
                                            />
                                        </div>
                                        <div className="sign-up-form-group">
                                            <label>Price ($)<span className="asterisk">*</span></label>
                                            <input
                                                type="number"
                                                name="price"
                                                min="0"
                                                step="0.01"
                                                value={editForm.price}
                                                onChange={handleEditChange}
                                                className={editErrors.price ? 'error' : ''}
                                            />
                                            {editErrors.price && <p className="sign-up-error">{editErrors.price}</p>}
                                        </div>
                                        <div className="vmi-edit-location-row">
                                            <div className="sign-up-form-group">
                                                <label htmlFor="vmi-edit-state">Item State<span className="asterisk">*</span></label>
                                                <MsvSelect
                                                    id="vmi-edit-state"
                                                    name="state"
                                                    fullWidth
                                                    filterable
                                                    placeholder="Type or select state…"
                                                    value={editForm.state}
                                                    options={STATE_OPTIONS}
                                                    onChange={handleEditChange}
                                                />
                                                {editErrors.state && <p className="sign-up-error">{editErrors.state}</p>}
                                            </div>
                                            <div className="sign-up-form-group">
                                                <label htmlFor="vmi-edit-city">Item City<span className="asterisk">*</span></label>
                                                <input
                                                    id="vmi-edit-city"
                                                    type="text"
                                                    name="city"
                                                    value={editForm.city}
                                                    onChange={handleEditChange}
                                                    className={editErrors.city ? 'error' : ''}
                                                />
                                                {editErrors.city && <p className="sign-up-error">{editErrors.city}</p>}
                                            </div>
                                        </div>
                                        <div className="vmi-edit-fulfillment-row">
                                            <div className="sign-up-form-group">
                                                <label>
                                                    Local pickup available
                                                    <input
                                                        type="checkbox"
                                                        name="localPickupAvailable"
                                                        checked={Boolean(editForm.localPickupAvailable)}
                                                        onChange={handleEditChange}
                                                    />
                                                </label>
                                            </div>
                                            <div className="sign-up-form-group">
                                                <label>
                                                    Shipping available
                                                    <input
                                                        type="checkbox"
                                                        name="shippingAvailable"
                                                        checked={Boolean(editForm.shippingAvailable)}
                                                        onChange={handleEditChange}
                                                    />
                                                </label>
                                            </div>
                                        </div>
                                        {editErrors.fulfillment && (
                                            <p className="sign-up-error">{editErrors.fulfillment}</p>
                                        )}
                                        <div className="sign-up-form-group">
                                            <label>Shipping cost ($)</label>
                                            <input
                                                type="number"
                                                name="shippingCost"
                                                min="0"
                                                step="0.01"
                                                value={editForm.shippingCost}
                                                onChange={handleEditChange}
                                                disabled={!editForm.shippingAvailable}
                                                aria-disabled={!editForm.shippingAvailable}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <p className="vmi-price-row">
                                            <strong>Price</strong>
                                            <span className="vmi-price">{formatPrice(price)}</span>
                                        </p>

                                        {item.description && (
                                            <p className="vci-description">{item.description}</p>
                                        )}

                                        <p><strong>Driver</strong> {item.driver || '—'}</p>
                                        <p><strong>Series</strong> {item.series || '—'}</p>
                                        <p><strong>Category</strong> {categoryLine || '—'}</p>
                                        <p><strong>Delivery</strong> {shippingLabel}</p>
                                        {placeLabel ? <p><strong>Location</strong> {placeLabel}</p> : null}
                                        {item.dataAddedFormatted ? (
                                            <p><strong>Listed</strong> {item.dataAddedFormatted}</p>
                                        ) : null}
                                    </>
                                )}
                            </div>
                        </div>

                        {!isEditing && (item.sellerTrust || showMessagePanel) && (
                            <div className="vmi-seller-block">
                                {item.sellerTrust && (
                                    <div className="vmi-seller-meta">
                                        <p><strong>Seller</strong></p>
                                        <SellerTrustSummary trust={item.sellerTrust} />
                                        {!isOwner && item.userId ? (
                                            <Link
                                                className="vmi-seller-collection-link"
                                                to={`/searchUsersCollection/${item.userId}`}
                                            >
                                                View seller&apos;s collection
                                            </Link>
                                        ) : null}
                                    </div>
                                )}

                                {showMessagePanel && (
                                    <div className={`vmi-message-panel${canMessage ? '' : ' is-preview'}`}>
                                        <label htmlFor="vmi-message-input">Message the seller</label>
                                        <div className="vmi-message-row">
                                            <input
                                                id="vmi-message-input"
                                                type="text"
                                                value={messageText}
                                                onChange={(e) => setMessageText(e.target.value)}
                                                placeholder="Hi, I am interested. Is this still available?"
                                                disabled={!canMessage}
                                            />
                                            <button
                                                type="button"
                                                className="view-button vci-btn-primary"
                                                onClick={handleMessageSeller}
                                                disabled={!canMessage || messagingSeller || !messageText.trim()}
                                            >
                                                {messagingSeller ? 'Opening…' : 'Send message'}
                                            </button>
                                        </div>
                                        {isOwner ? (
                                            <p className="vmi-message-hint">
                                                Buyers will see this when your listing is live. You can&apos;t message yourself.
                                            </p>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </MsvDetailShell>

            <ConfirmDialog
                open={showRelistConfirm}
                variant="success"
                title="Relist this item?"
                message="This puts your listing back on the marketplace for 30 days. Buyers will be able to see it and message you again."
                confirmLabel="Relist for 30 days"
                cancelLabel="Cancel"
                busy={relistingListing}
                onConfirm={confirmRelistListing}
                onCancel={() => !relistingListing && setShowRelistConfirm(false)}
            />

            <ConfirmDialog
                open={showPublishConfirm}
                variant="success"
                title="Resubmit this listing?"
                message="This sends your listing back to admin for approval. It will not appear on the marketplace until it is approved."
                confirmLabel="Resubmit for approval"
                cancelLabel="Cancel"
                busy={publishingListing}
                onConfirm={confirmPublishListing}
                onCancel={() => !publishingListing && setShowPublishConfirm(false)}
            />

            <ConfirmDialog
                open={showMoveToCollectionConfirm}
                title={status === 'UnderReview' ? 'Cancel listing?' : 'Delete listing?'}
                message={
                    status === 'UnderReview'
                        ? 'This cancels admin review. The item stays in your collection.'
                        : 'This deletes the listing from the marketplace. The item stays in your collection.'
                }
                confirmLabel={status === 'UnderReview' ? 'Cancel listing' : 'Delete listing'}
                cancelLabel="Keep listing"
                busyLabel={status === 'UnderReview' ? 'Cancelling…' : 'Deleting…'}
                busy={movingToCollection}
                onConfirm={confirmMoveToCollection}
                onCancel={() => !movingToCollection && setShowMoveToCollectionConfirm(false)}
            />

            <MarkAsSoldDialog
                open={showMarkSoldDialog}
                listingTitle={toTitleCase(item.title)}
                busy={markingSold}
                onConfirm={confirmMarkSold}
                onCancel={() => !markingSold && setShowMarkSoldDialog(false)}
            />

            <ConfirmDialog
                open={showRemoveConfirm}
                title="Pause listing?"
                message={
                    <>
                        This takes &quot;{toTitleCase(item.title)}&quot; down from the marketplace, but it will stay in{' '}
                        <strong>My Listings</strong> if you want to relist it later.
                    </>
                }
                confirmLabel="Pause listing"
                cancelLabel="Cancel"
                busyLabel="Pausing…"
                busy={removingListing}
                onConfirm={confirmRemoveListing}
                onCancel={() => !removingListing && setShowRemoveConfirm(false)}
            />

            {showDescriptionPopup && createPortal(
                <div
                    className="vmi-description-popup-overlay"
                    role="presentation"
                    onClick={closeDescriptionPopup}
                >
                    <div
                        className="vmi-description-popup"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="vmi-description-popup-title"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="vmi-description-popup-head">
                            <h3 id="vmi-description-popup-title">Description</h3>
                            <span className="vmi-char-count">{descriptionDraft.length}/500</span>
                        </div>
                        <textarea
                            ref={descriptionPopupRef}
                            rows={6}
                            maxLength={500}
                            value={descriptionDraft}
                            onChange={(event) => setDescriptionDraft(event.target.value)}
                            className="vmi-description-popup-textarea"
                            placeholder="Add details buyers should know about this item…"
                        />
                        <div className="vmi-description-popup-actions">
                            <button
                                type="button"
                                className="view-button"
                                onClick={closeDescriptionPopup}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="view-button vci-btn-primary"
                                onClick={saveDescriptionPopup}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default ViewMarketplaceItem;
